// src/pages/Login.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import s from "../modules/Login.module.css";
import { useT, useI18nReady } from "../components/TextProvider";

/**
 * ЛОГИН + Сохранение токенов и профиля пользователя
 * - POST на AUTH_ENDPOINT с { username, password }
 * - Сохранение токенов в localStorage/sessionStorage
 * - Если user не пришёл в ответе — отдельный GET на USER_ENDPOINT
 * - Нормализация профиля: pk -> id (pk удаляем)
 * - Сохранение профиля в auth:user
 * - Редирект на /profile
 */

// Настройте под свой бэкенд
const AUTH_ENDPOINT =
  process.env.REACT_APP_AUTH_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/v1/dj-rest-auth/login/";

const USER_ENDPOINT =
  process.env.REACT_APP_USER_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/v1/dj-rest-auth/user/";

// Куда перенаправлять после успешного входа
const PROFILE_ROUTE = "/";

/** Нормализация объекта пользователя: переводит pk -> id и убирает pk */
function normalizeUser(user) {
  if (!user || typeof user !== "object") return user;
  if ("pk" in user) {
    const { pk, ...rest } = user;
    // если id уже есть — приоритет за id; иначе кладём pk в id
    return { ...rest, id: user.id ?? pk };
  }
  // если pk нет, но id тоже не пришёл — оставляем как есть
  return user;
}

/** Универсальное сохранение токенов/профиля: поддержка разных форматов ответа */
function persistAuth(data, remember) {
  const storage = remember ? window.localStorage : window.sessionStorage;

  const pairs = Object.entries(data || {});
  let somethingStored = false;

  for (const [k, v] of pairs) {
    if (v == null) continue;
    const key = String(k).toLowerCase();

    // частые варианты ключей токенов
    if (["access", "refresh", "token", "key", "jwt", "auth_token"].includes(key)) {
      storage.setItem(`auth:${key}`, String(v));
      somethingStored = true;
    }
    // если API сразу вернул user — нормализуем и сохраняем
    if (key === "user" && typeof v === "object") {
      const normalized = normalizeUser(v);
      storage.setItem("auth:user", JSON.stringify(normalized));
    }
  }

  // fallback: сохраним оригинальный payload, если формат неожиданный
  if (!somethingStored) {
    storage.setItem("auth:payload", JSON.stringify(data));
  }
}

/** Достаёт из data любой доступный токен для последующего запроса профиля */
function pickAnyToken(data) {
  if (!data) return null;
  return (
    data.access || // JWT (доступ)
    data.token ||  // DRF Token Auth
    data.key ||    // dj-rest-auth ключ
    data.jwt ||    // иной jwt
    data.auth_token || null
  );
}

/** Формирует корректный Authorization в зависимости от типа токена */
function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);

  // Можно принудительно задать схему через REACT_APP_AUTH_SCHEME = "Bearer" | "Token" | "JWT"
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME;
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };

  // Эвристика: JWT обычно начинается с "eyJ" → Bearer; иначе чаще нужна "Token"
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

/** Загружает профиль пользователя, нормализует pk->id и сохраняет */
async function fetchAndStoreUser({ remember, tokenLike } = {}) {
  const storage = remember ? window.localStorage : window.sessionStorage;

  const headers = {
    "Content-Type": "application/json",
    ...(tokenLike ? makeAuthHeader(tokenLike) : {}),
  };

  const res = await fetch(USER_ENDPOINT, {
    method: "GET",
    headers,
    credentials: "include",
  });

  if (!res.ok) return null;

  const user = await res.json().catch(() => null);
  if (user && typeof user === "object") {
    const normalized = normalizeUser(user);
    storage.setItem("auth:user", JSON.stringify(normalized));
  }
  return user;
}

export default function Login() {
  const navigate = useNavigate();
  
    const t = useT();
    const ready = useI18nReady();

  const [login, setLogin] = useState(""); // email/телефон/username
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!login.trim() || !pwd) {
      setError("Login va parolni kiriting.");
      return;
    }

    try {
      setLoading(true);

      // Популярные схемы полей: username/email/phone — отправим username
      const body = {
        username: login.trim(),
        // email: login.trim(),
        // phone: login.trim(),
        password: pwd,
      };

      const res = await fetch(AUTH_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // оставляем: на некоторых бэках выдаются cookie
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const serverMsg =
          (typeof data === "string" && data) ||
          data?.detail ||
          data?.error ||
          data?.message ||
          (data && Array.isArray(Object.values(data)[0]) ? Object.values(data)[0] : null) ||
          "Kirishda xatolik. Ma'lumotlaringizni tekshiring.";

        throw new Error(
          Array.isArray(serverMsg) ? serverMsg.join(" ") : String(serverMsg)
        );
      }

      // 1) Сохраняем токены и, если пришёл user, нормализуем pk->id
      persistAuth(data, remember);

      // 2) Если пользователя нет в ответе — подтянем отдельно
      if (!data?.user) {
        const tokenLike = pickAnyToken(data);
        await fetchAndStoreUser({ remember, tokenLike });
      } else {
        // На случай, если пришёл user с pk, но persistAuth не сработал (редкие форматы)
        const storage = remember ? window.localStorage : window.sessionStorage;
        const saved = storage.getItem("auth:user");
        if (!saved && typeof data.user === "object") {
          const normalized = normalizeUser(data.user);
          storage.setItem("auth:user", JSON.stringify(normalized));
        }
      }

      // 3) Редирект
      // Сообщим приложению, что auth-состояние изменилось
      window.dispatchEvent(new Event("auth:changed"));
      navigate(PROFILE_ROUTE, { replace: true });
    } catch (err) {
      setError(err?.message || "Kirib bo'lmadi.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={s.wrapper}>
      <div className={s.card} role="region" aria-label="Login">
        <h1 className={s.title}>{t('kirish')}</h1>

        <form className={s.form} onSubmit={handleSubmit} noValidate>
          {/* Логин (email/телефон/username) */}
          <div className={s.field}>
            <input
              className={s.input}
              type="text"
              inputMode="email"
              autoComplete="username"
              placeholder=" "
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              required
              aria-label={t('loginEmail')}
            />
            <span className={s.label}>{t('loginEmail')}</span>
          </div>

          {/* Пароль */}
          <div className={s.field}>
            <input
              className={s.input}
              type={showPwd ? "text" : "password"}
              autoComplete="current-password"
              placeholder=" "
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              required
              aria-label="Parol"
            />
            <span className={s.label}>{t('parol')}</span>

            <span className={s.trailing}>
              <button
                type="button"
                className={s.eyeBtn}
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? "Yashirish" : "Ko'rsatish"}
                title={showPwd ? "Yashirish" : "Ko'rsatish"}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                  {showPwd ? (
                    <path
                      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Zm10 4a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm8.485-10.485-16.97 16.97"
                      fill="none" stroke="currentColor" strokeWidth="1.6"
                      strokeLinecap="round" strokeLinejoin="round"
                    />
                  ) : (
                    <>
                      <path
                        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
                        fill="none" stroke="currentColor" strokeWidth="1.6"
                        strokeLinecap="round" strokeLinejoin="round"
                      />
                      <circle
                        cx="12" cy="12" r="4"
                        fill="none" stroke="currentColor" strokeWidth="1.6"
                      />
                    </>
                  )}
                </svg>
              </button>
            </span>
          </div>

          {/* Запомнить меня + ссылки */}
          <div className={s.row}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              {t('eslabQolish')}
            </label>

            <Link to="/forgot" className={s.mutedLink}>{t('unutdingizmi')}</Link>
          </div>

          {error ? <div className={s.error}>{error}</div> : null}

          <button
            className={s.submit}
            type="submit"
            disabled={loading || !login || !pwd}
          >
            {loading ? "Kirish…" : "Kirish"}
          </button>
        </form>

        <div className={s.footer}>
          {t('akkauntizgizYuqmi')}{" "}
          <Link to="/register" className={s.link}>{t('ruyxatdanUtish')}</Link>
        </div>
      </div>
    </div>
  );
}
